import { SessionService } from '../session/session.service';
import { Injectable, Optional, SkipSelf } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { MatSnackBar } from '@angular/material/snack-bar'
import { TranslateService } from '@ngx-translate/core';
import { AppConfigService } from '../app-config/app-config.service';

export interface ErrorResponse {
    property: string;
    message: string;
}

export interface ApiSuccessResponse<T> {
    success: true;
    message?: string;
    messageCode?: number;
    data: T;
    statusCode?: number;
}

export interface ApiErrorResponse {
    success: false;
    messageCode: number;
    message?: string;
    data: ErrorResponse[];
    statusCode?: number;
}

type ApiResponse<T> = ApiSuccessResponse<T> | ApiErrorResponse;

@Injectable({
    providedIn: 'root'
})
export class ApiService {

    constructor(
        @Optional() @SkipSelf() parentModule: ApiService,
        private http: HttpClient,
        private session: SessionService,
        private snackBar: MatSnackBar,
        private translateService: TranslateService,
        private appConfig: AppConfigService) {
        if (parentModule) {
            throw new Error('ApiService is already loaded');
        }
    }

    get<T>(url: string, queryParams?: any): Promise<ApiResponse<T>> {
        return this.request('GET', url, null, queryParams);
    }

    post<T>(url: string, data: any): Promise<ApiResponse<T>> {
        return this.request('POST', url, data);
    }

    put<T>(url: string, data: any): Promise<ApiResponse<T>> {
        return this.request('PUT', url, data);
    }

    patch<T>(url: string, data: any): Promise<ApiResponse<T>> {
        return this.request('PATCH', url, data);
    }

    delete<T>(url: string): Promise<ApiResponse<T>> {
        return this.request('DELETE', url, null);
    }

    private getPartner(): string {
        return this.appConfig.partnerKey;
    }

    private async request<T>(method: string, url: string, data?: any, queryParams?: any): Promise<ApiResponse<T>> {
        if (url[0] !== '/') {
            throw new Error('Missing leading slash in ' + url);
        }

        let urlSearchParams: URLSearchParams | undefined = undefined;;
        if (queryParams) {
            urlSearchParams = new URLSearchParams();
            const keys: string[] = Object.keys(queryParams);
            const values: string[] = Object.values(queryParams);

            for (let index = 0; index < keys.length; index++) {
                if (Array.isArray(values[index])) {
                    queryParams[keys[index]].forEach((value: any) => {
                        urlSearchParams?.append(keys[index], value);
                    });
                }
                else {
                    if (typeof(values[index]) == 'undefined') {
                        values[index] = '';
                    }
                    urlSearchParams.append(keys[index], values[index]);
                }
            }
        }

        let fullUrl = environment.apiUrl + url;

        if (urlSearchParams)
            fullUrl = fullUrl + '?' + urlSearchParams.toString();

        const options: any = {
            body: data,
            headers: {},
        };

        options.headers['X-Partner'] = this.getPartner();

        if (this.session.token) {
            options.headers['Authorization'] = "Bearer " + this.session.token;
        }
        if (this.session.isLogged && this.session.getDetails().language)
            options.headers['Accept-Language'] = this.session.getDetails().language;

        try {
            const res: any = await this.http.request<ApiResponse<T>>(method, fullUrl, {
                observe: 'response',
                ...options,
            }).toPromise();
            return res.body;
        } catch (error: any) {
            if(error.status == 404 || error.status == 0) {
                this.snackBar.open(this.translateService.instant("api.notFound"), '', {
                    duration: 10000,
                    verticalPosition: 'top',
                    panelClass: ['red-snackbar']
                });
            } else if(error.status == 500) {
                this.snackBar.open(this.translateService.instant("api.internalServerError"), '', {
                    duration: 10000,
                    verticalPosition: 'top',
                    panelClass: ['red-snackbar']
                });
            }
            if (error.error instanceof ErrorEvent) {
                return {
                    success: false,
                    message: 'API request failed',
                    messageCode: -1,
                    data: []
                };
            } else {
                return error.error;
            }
        }
    }
}
